import { useCallback, useEffect, useState } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PING_URL = '/favicon.png';

async function serverReachable(): Promise<boolean> {
  try {
    const res = await fetch(`${PING_URL}?ping=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-store',
    });
    return res.ok || res.type === 'opaque';
  } catch {
    return false;
  }
}

/**
 * Aviso de conexão. Não confia apenas em navigator.onLine: confirma com o
 * servidor antes de dizer que está offline.
 */
export function ConnectionStatusBanner() {
  const [offline, setOffline] = useState(false);
  const [checking, setChecking] = useState(false);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        setOffline(true);
        return;
      }
      setOffline(!(await serverReachable()));
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    const onOffline = () => setOffline(true);
    const onOnline = () => void check();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };

    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check]);

  if (!offline) return null;

  return (
    <div className="fixed bottom-4 left-1/2 z-[310] flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 items-center gap-3 rounded-xl border border-destructive/40 bg-card p-3 shadow-lg">
      <WifiOff className="h-5 w-5 shrink-0 text-destructive" />
      <p className="min-w-0 flex-1 text-xs text-muted-foreground">
        Sem conexão com o servidor. Alterações não serão salvas até voltar.
      </p>
      <Button size="sm" variant="outline" onClick={() => void check()} disabled={checking}>
        <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${checking ? 'animate-spin' : ''}`} />
        Tentar
      </Button>
    </div>
  );
}
